import { Component, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of, Subject, map, switchMap } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import { setLicenseKey } from 'survey-core';
import { environment } from 'src/environments/environment';
import { AppInsightsService } from './core/services/app-insights.service';
import { ToggleDarkMode } from './core/state/ui.actions';
import { ClarityService } from './core/services/clarity.service';
import { CookieConsentService } from './core/services/cookie-consent.service';
import { LocalStorageService } from './core/services/local-storage.service';
import { LanguageService } from './core/services/language.service';
import { MatDateLocaleSyncService } from './core/services/mat-date-locale-sync.service';
import { UiState } from './core/state/ui.state';
import { REDIRECT_PATH_KEY } from './services/store-snapshot.service';
import { ExternalDocumentRedirectService } from './document-user/services/external-document-redirect.service';
import {
  ChangeCurrentSelectedDepartment,
  ChangeCurrentSelectedTenant,
} from './tenant-selector/state/tenant-user-access.actions';
import { TenantUserAccessState } from './tenant-selector/state/tenant-user-access.state';

type RedirectData = {
  externalLink: boolean;
  tenantId: string;
  departmentId: string;
  path: string | undefined | null;
};

const DARKMODE = 'darkMode';
const LIGHTMODE = 'lightMode';
const CC_DARKMODE = 'cc--darkmode';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isDarkMode$: Observable<boolean> = this.store.select(UiState.isDarkMode);
  isToolbarCollapsed$: Observable<boolean> = this.store.select(UiState.isToolbarCollapsed);
  @HostBinding('class') className: string;

  title = 'ccomx-client';
  isAuthenticated = false;
  private readonly destroying$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private store: Store,
    private localStorageService: LocalStorageService,
    private languageService: LanguageService,
    private matDateLocaleSyncService: MatDateLocaleSyncService,
    private oidcSecurityService: OidcSecurityService,
    private router: Router,
    private clarityService: ClarityService,
    private cookieConsentService: CookieConsentService,
    private appInsightsService: AppInsightsService,
    private externalDocumentRedirectService: ExternalDocumentRedirectService
  ) {
    this.translate.addLangs(['en', 'no', 'se', 'is', 'es']);
  }

  ngOnInit(): void {
    this.languageService.initializeLanguage();
    this.matDateLocaleSyncService.initialize();
    this.cookieConsentService.init();

    setLicenseKey(environment.surveyJSKey);
    this.oidcSecurityService
      .checkAuth()
      .pipe(
        switchMap(({ isAuthenticated, userData }) => {
          this.isAuthenticated = isAuthenticated;
          if (!isAuthenticated) {
            this.oidcSecurityService.authorize();
            return EMPTY;
          }
          this.identifyUserInClarity();
          const userId = userData?.['sub'] ?? userData?.['oid'] ?? userData?.['preferred_username'];
          if (userId) {
            this.appInsightsService.setAuthenticatedUserContext(String(userId));
          }
          return of(this.prepareRedirectData());
        }),
        switchMap(this.redirect())
      )
      .subscribe();

    this.subscribeToTheme();
    this.checkState();
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  private redirect() {
    return (redirectData: RedirectData) => {
      if (redirectData.externalLink && redirectData.path) {
        return this.handleExternalRedirect(redirectData);
      }

      return this.navigateToPath(redirectData.path);
    };
  }

  private navigateAndClearPathKey(path: string | null | undefined) {
    this.router.navigateByUrl(path ?? '/').then(() => sessionStorage.removeItem(REDIRECT_PATH_KEY));
  }

  private handleExternalRedirect({
    tenantId,
    departmentId,
    path,
  }: RedirectData): Observable<string | null | undefined> {
    return this.selectTenantIfNeeded(tenantId).pipe(
      switchMap(() => this.externalDocumentRedirectService.resolve(path, departmentId)),
      switchMap((redirectResult) =>
        this.selectDepartmentIfNeeded(redirectResult.departmentId).pipe(
          map(() => redirectResult.path)
        )
      ),
      tap((resolvedPath) => this.navigateAndClearPathKey(resolvedPath))
    );
  }

  private navigateToPath(path: string | null | undefined): Observable<string | null | undefined> {
    return of(path).pipe(tap((resolvedPath) => this.navigateAndClearPathKey(resolvedPath)));
  }

  private selectTenantIfNeeded(tenantId: string): Observable<unknown> {
    if (!tenantId || TenantUserAccessState.getLastSelectedTenantId() === tenantId) {
      return of(null);
    }

    return this.store.dispatch(new ChangeCurrentSelectedTenant(tenantId, false));
  }

  private selectDepartmentIfNeeded(departmentId: string | null): Observable<unknown> {
    if (!departmentId || TenantUserAccessState.getLastSelectedDepartmentId() === departmentId) {
      return of(null);
    }

    return this.store.dispatch(new ChangeCurrentSelectedDepartment(departmentId, false));
  }

  private prepareRedirectData(): RedirectData {
    const item = sessionStorage.getItem(REDIRECT_PATH_KEY);
    const path = item;
    const externalLink = !!item?.includes('/ext/');
    const data = { externalLink, tenantId: '', departmentId: '', path };
    if (externalLink && item) {
      const tenantIdDepartmentIdPath = item.slice(item.indexOf('/ext/') + 5);
      data.tenantId = tenantIdDepartmentIdPath.slice(0, 36);
      data.departmentId = tenantIdDepartmentIdPath.slice(37, 73);
      data.path = tenantIdDepartmentIdPath.slice(73);
    }
    return data;
  }

  private identifyUserInClarity(): void {
    const userAccess = this.store.selectSnapshot(TenantUserAccessState.userAccess);
    if (userAccess?.oid) {
      this.clarityService.identify(userAccess.oid, undefined, undefined, userAccess.name);
    }
  }

  private checkState() {
    let mode = false;
    const localtheme = this.localStorageService.getSavedState('theme');
    if (localtheme === DARKMODE) {
      this.className = DARKMODE;
      mode = true;
    } else {
      this.className = LIGHTMODE;
    }
    this.store.dispatch(new ToggleDarkMode(mode));
  }

  private subscribeToTheme() {
    this.isDarkMode$
      .pipe(
        takeUntil(this.destroying$),
        tap((isDarkMode) => {
          const htmlBodyClasses = document.body.classList;
          const htmlElementClasses = document.documentElement.classList;
          if (isDarkMode) {
            this.className = DARKMODE;
            if (!htmlBodyClasses.contains(DARKMODE)) {
              htmlBodyClasses.add(DARKMODE);
            }
            htmlElementClasses.add(CC_DARKMODE);
          } else {
            this.className = LIGHTMODE;
            if (htmlBodyClasses.contains(DARKMODE)) {
              htmlBodyClasses.remove(DARKMODE);
            }
            htmlElementClasses.remove(CC_DARKMODE);
          }
        })
      )
      .subscribe();

    this.isToolbarCollapsed$
      .pipe(
        takeUntil(this.destroying$),
        tap((isToolbarCollapsed) => {
          const htmlBodyClasses = document.body.classList;
          htmlBodyClasses.toggle('toolbar-collapsed', isToolbarCollapsed);
        })
      )
      .subscribe();
  }
}
